import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED, fmtNum } from '../lib/format';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { TrendingUp, ShoppingBag, Receipt, Grid3x3, AlertTriangle, Sparkles } from 'lucide-react';
import PageHelp from '../components/PageHelp';

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

const TONES = {
  primary: 'bg-primary/10 text-primary',
  warning: 'bg-warning/10 text-warning',
  success: 'bg-success/10 text-success',
};

const Kpi = ({ icon: Icon, label, value, sub, tone = 'primary', testid }) => (
  <Card className="p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${TONES[tone]}`}><Icon className="h-4 w-4" /></div>
    </div>
    <div className="text-2xl font-display font-bold mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

export default function Dashboard() {
  const { currentOutlet, user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/reports/dashboard', { params: currentOutlet ? { outlet_id: currentOutlet.id } : {} });
        setD(data);
      } catch (e) {} finally { setLoading(false); }
    })();
  }, [currentOutlet]);

  if (loading || !d) return <div className="p-6 text-muted-foreground">Loading dashboard…</div>;

  const channelData = Object.entries(d.channels || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHelp id="dashboard">This is your live overview. Use the outlet selector (top-right) to switch branches, and the <b>Guide</b> button anytime to replay the feature tour. Start billing from <b>POS Billing</b> in the sidebar.</PageHelp>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Welcome, {user?.name} · {currentOutlet?.name || 'All Outlets'}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi testid="kpi-today-sales" icon={TrendingUp} label="Today's Sales" value={fmtAED(d.today_sales)} sub={`${d.today_orders} orders today`} />
        <Kpi testid="kpi-total-sales" icon={Receipt} label="Total Sales" value={fmtAED(d.total_sales)} sub={`${d.total_orders} orders`} />
        <Kpi testid="kpi-aov" icon={ShoppingBag} label="Avg Order Value" value={fmtAED(d.aov)} />
        <Kpi testid="kpi-live-tables" icon={Grid3x3} label="Live Tables" value={fmtNum(d.live_tables)} sub="occupied now" />
        <Kpi testid="kpi-low-stock" icon={AlertTriangle} label="Low Stock" value={fmtNum(d.low_stock_count)} sub="items to reorder" tone="warning" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2">
          <div className="font-display font-semibold mb-3">Sales — Last 7 Days</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.daily_series}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => fmtAED(v)} />
              <Line type="monotone" dataKey="sales" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4">
          <div className="font-display font-semibold mb-3">Sales by Channel</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={channelData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                {channelData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtAED(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-display font-semibold mb-3">Top Selling Items</div>
          <div className="space-y-2">
            {(d.top_items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between text-sm" data-testid={`top-item-${i}`}>
                <div className="flex items-center gap-2"><span className="text-muted-foreground w-5">{i + 1}.</span>{it.name}</div>
                <Badge variant="secondary">{it.qty} sold</Badge>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-4">
          <div className="font-display font-semibold mb-3">Hourly Sales Heatmap</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.hourly}>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v) => fmtAED(v)} labelFormatter={(h) => `${h}:00`} />
              <Bar dataKey="sales" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
